import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`${API}/gallery`);
        const data = await response.json();
        setPosts(data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Sample gallery images removed - showing Coming Soon instead
  const sampleImages = [];

  const getGridClass = (type) => {
    switch (type) {
      case 'hero': return 'bento-hero';
      case 'tall': return 'bento-tall';
      case 'wide': return 'bento-wide';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="gallery-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-primary uppercase tracking-widest text-sm mb-4"
            >
              Our Work
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold tracking-tight mb-6"
            >
              <span className="gradient-text">Gallery</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-muted-foreground text-lg max-w-2xl mx-auto"
            >
              Browse through our collection of stunning snake photoshoots
            </motion.p>
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="bento-grid">
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`rounded-xl image-loading ${i === 0 ? 'bento-hero' : ''}`} />
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="bento-grid">
              {posts.map((post, index) => (
                <motion.div
                  key={post.post_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={index === 0 ? 'bento-hero' : index === 2 ? 'bento-tall' : index === 3 ? 'bento-wide' : ''}
                >
                  <Card
                    className="h-full overflow-hidden border-white/10 card-hover cursor-pointer group"
                    data-testid={`gallery-post-${post.post_id}`}
                  >
                    <div className="relative h-full">
                      <img
                        src={post.image_urls[0]}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <h3 className="text-xl font-semibold text-white">{post.title}</h3>
                        {post.description && (
                          <p className="text-white/70 text-sm mt-1 line-clamp-2">{post.description}</p>
                        )}
                        <p className="text-primary text-sm mt-2">By {post.photographer_name}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            // Coming Soon message when no posts exist
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-24"
            >
              <Card className="max-w-lg mx-auto bg-card/50 backdrop-blur-md border-white/10 p-12">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg className="w-12 h-12 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-3xl font-bold gradient-text mb-4">Coming Soon</h3>
                <p className="text-muted-foreground text-lg mb-6">
                  We're building an amazing collection of exotic snake photoshoots. Check back soon!
                </p>
                <p className="text-sm text-muted-foreground">
                  Photographers can sign in to post their work to the gallery.
                </p>
              </Card>
            </motion.div>
          )}

          {/* Empty State Message removed - Coming Soon is shown above */}
        </div>
      </div>
    </div>
  );
};

export default GalleryPage;
